import React, { useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import EmployeeLayout from "./EmployeeLayout";
import { toast } from "../utils/toast";

const followupStatusClasses = {
  pending: "bg-amber-100 text-amber-700",
  callback: "bg-sky-100 text-sky-700",
  completed: "bg-emerald-100 text-emerald-700",
  qualified: "bg-violet-100 text-violet-700",
  closed: "bg-slate-100 text-slate-700",
  not_interested: "bg-rose-100 text-rose-700",
};

const followupStatusOptions = [
  { value: "pending", label: "Pending" },
  { value: "callback", label: "Callback" },
  { value: "completed", label: "Completed" },
  { value: "qualified", label: "Qualified" },
  { value: "closed", label: "Closed" },
  { value: "not_interested", label: "Not Interested" },
];

const followupTypeOptions = [
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
  { value: "whatsapp", label: "WhatsApp" },
];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    return user?.id || 1;
  } catch {
    return 1;
  }
};

const formatStatus = (value) =>
  String(value || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const toApiDateTime = (value) => (value ? `${value.replace("T", " ")}:00` : "");

const defaultEditForm = {
  id: "",
  lead_id: "",
  type: "call",
  follow_up_status: "pending",
  reminder_at: "",
  next_follow_up_date: "",
  message: "",
};

export default function EmployeeFollowups() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(defaultEditForm);

  const loadItems = async () => {
    try {
      const query = new URLSearchParams({ user_id: String(getCurrentUserId()) }).toString();
      const response = await fetch(`${ApiURL}crm/employee/get_followups_by_user_id.php?${query}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to load follow-ups");
      }
      setItems(result?.data || []);
    } catch (error) {
      console.error("Error loading follow-ups:", error);
      toast.error(error.message || "Failed to load follow-ups");
      setItems([]);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter((item) =>
      [
        item.id,
        item.lead_id,
        item.type,
        item.follow_up_status,
        item.message,
        item.lead?.customer_name,
        item.lead?.customer_number,
        item.lead?.lead_status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [items, search]);

  const summary = useMemo(() => {
    return filteredItems.reduce(
      (accumulator, item) => {
        accumulator.total += 1;
        if (["pending", "callback"].includes(item.follow_up_status)) {
          accumulator.pending += 1;
        }
        if (["completed", "qualified", "closed"].includes(item.follow_up_status)) {
          accumulator.completed += 1;
        }
        if (item.follow_up_status === "callback") {
          accumulator.callbacks += 1;
        }
        return accumulator;
      },
      { total: 0, pending: 0, completed: 0, callbacks: 0 }
    );
  }, [filteredItems]);

  const openEditModal = (item) => {
    setEditForm({
      id: String(item.id),
      lead_id: String(item.lead_id),
      type: item.type || "call",
      follow_up_status: item.follow_up_status || "pending",
      reminder_at: toInputDateTime(item.reminder_at),
      next_follow_up_date: toInputDateTime(item.next_follow_up_date),
      message: item.message || "",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditForm(defaultEditForm);
  };

  const handleSave = async () => {
    if (!editForm.id || !editForm.message.trim()) {
      toast.warning("Please add a follow-up note before saving.");
      return;
    }

    if (!editForm.reminder_at) {
      toast.warning("Please choose a follow-up date.");
      return;
    }

    if (editForm.follow_up_status === "callback" && !editForm.next_follow_up_date) {
      toast.warning("Please set the next follow-up date for callback status.");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${ApiURL}crm/employee/update_followup.php`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          follow_up_id: Number(editForm.id),
          type: editForm.type,
          follow_up_status: editForm.follow_up_status,
          reminder_at: toApiDateTime(editForm.reminder_at),
          next_follow_up_date:
            editForm.follow_up_status === "callback" ? toApiDateTime(editForm.next_follow_up_date) : "",
          message: editForm.message.trim(),
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to update follow-up");
      }

      toast.success("Follow-up updated successfully.");
      closeEditModal();
      await loadItems();
    } catch (error) {
      console.error("Error updating follow-up:", error);
      toast.error(error.message || "Failed to update follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <EmployeeLayout
      title="Follow-up Report"
      description="Track your follow-ups, update their status, and move them out of pending when the action is done."
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Total Follow-ups</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Pending</div>
            <div className="mt-2 text-3xl font-bold text-amber-700">{summary.pending}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Completed</div>
            <div className="mt-2 text-3xl font-bold text-emerald-700">{summary.completed}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Callbacks</div>
            <div className="mt-2 text-3xl font-bold text-sky-700">{summary.callbacks}</div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Manage Follow-ups</h3>
              <p className="mt-1 text-sm text-slate-500">
                `Pending` means the action is still open. Update it to `Completed`, `Callback`, `Qualified`, `Closed`, or `Not Interested` after you act on it.
              </p>
            </div>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search candidate, status, type..."
              className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm md:max-w-sm"
            />
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">ID</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Lead</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Follow-up Date</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Next Follow-up</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Message</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-3 text-slate-900">#{item.id}</td>
                    <td className="px-3 py-3 text-slate-900">
                      <div className="font-medium">{item.lead?.customer_name || "-"}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{item.lead?.customer_number || "-"}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>#{item.lead_id}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{item.lead?.lead_status || "-"}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{formatStatus(item.type)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${followupStatusClasses[item.follow_up_status] || "bg-slate-100 text-slate-700"}`}>
                        {formatStatus(item.follow_up_status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{formatDateTime(item.reminder_at)}</td>
                    <td className="px-3 py-3 text-slate-600">{formatDateTime(item.next_follow_up_date)}</td>
                    <td className="px-3 py-3 text-slate-600 max-w-[260px] whitespace-pre-wrap">{item.message || "-"}</td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => openEditModal(item)}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      No follow-ups found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showEditModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Update Follow-up</h3>
                <p className="mt-1 text-xs text-gray-500">Change status after the recruiter actually performs the follow-up.</p>
              </div>
              <button
                type="button"
                onClick={closeEditModal}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                x
              </button>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Type</label>
                  <select
                    value={editForm.type}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, type: event.target.value }))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  >
                    {followupTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
                  <select
                    value={editForm.follow_up_status}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        follow_up_status: event.target.value,
                        next_follow_up_date: event.target.value === "callback" ? prev.next_follow_up_date : "",
                      }))
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  >
                    {followupStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Follow-up Date</label>
                <input
                  type="datetime-local"
                  value={editForm.reminder_at}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, reminder_at: event.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {editForm.follow_up_status === "callback" ? (
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Next Follow-up Date</label>
                  <input
                    type="datetime-local"
                    value={editForm.next_follow_up_date}
                    onChange={(event) => setEditForm((prev) => ({ ...prev, next_follow_up_date: event.target.value }))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              ) : null}

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  rows={4}
                  value={editForm.message}
                  onChange={(event) => setEditForm((prev) => ({ ...prev, message: event.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Write the outcome of this follow-up..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={closeEditModal}
                className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Update"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </EmployeeLayout>
  );
}
